/**
 */

import type {
  AgentReportEntity,
  ChatMessageEntity,
  ContentBriefEntity,
  DelegationEntity,
  MessageEntity,
  NoteEntity,
  ToolCallEntity,
} from '@saaga/shared/store/slices/chat-message-entity.interface'
import type {
  ChatAgentReport,
  ChatDelegation,
  ChatItem,
  ChatMessage,
  ChatMessageAttachment,
  ChatNote,
  ChatToolCall,
} from '@/components/chat/chat-message.interface'

type MessageLikeEntity = MessageEntity | ContentBriefEntity

const resolveItemTimestamp = (entity: ChatMessageEntity): string =>
  entity.turnStartedAt ?? entity.timestamp

const mapEntityAttachments = (entity: MessageEntity): ChatMessageAttachment[] | undefined => {
  if (!entity.attachmentList?.length) return undefined
  return entity.attachmentList.map((a) => ({
    id: a.id,
    filename: a.filename,
    mimeType: a.mime_type,
    sizeBytes: a.size_bytes,
  }))
}

const isMessageActivelyStreaming = (entity: MessageLikeEntity): boolean =>
  Boolean(entity.isStreaming && entity.sender !== 'user')

const transformEntityToMessage = (entity: MessageLikeEntity): ChatMessage => ({
  id: entity.id,
  type: 'message',
  sender: entity.sender,
  content: isMessageActivelyStreaming(entity)
    ? entity.streamingContent || entity.content
    : entity.content,
  timestamp: resolveItemTimestamp(entity),
  stableKey: entity.stableKey,
  participant: entity.participant,
  attachmentList: entity.type === 'message' ? mapEntityAttachments(entity) : undefined,
  isStreaming: isMessageActivelyStreaming(entity),
  isSending: entity.isPending,
  errorMessage: entity.errorMessage,
})

const transformEntityToNote = (entity: NoteEntity): ChatNote => ({
  id: entity.id,
  type: 'note',
  content: entity.content,
  timestamp: resolveItemTimestamp(entity),
  stableKey: entity.stableKey,
  participant: entity.participant ?? { id: 'unknown', name: 'Unknown' },
})

const transformEntityToToolCall = (entity: ToolCallEntity): ChatToolCall => ({
  id: entity.id,
  type: 'tool-call',
  toolName: entity.toolName,
  friendlyName: entity.friendlyName,
  status: entity.toolStatus,
  input: entity.toolInput,
  output: entity.toolOutput,
  errorMessage: entity.toolErrorMessage,
  timestamp: resolveItemTimestamp(entity),
  stableKey: entity.stableKey,
  participant: entity.participant,
  htmlResultKey: entity.htmlResultKey,
  progressMessage: entity.toolProgressMessage,
  progressPercentage: entity.toolProgressPercentage,
})

const transformEntityToAgentReport = (entity: AgentReportEntity): ChatAgentReport => ({
  id: entity.id,
  type: 'agent-report',
  timestamp: resolveItemTimestamp(entity),
  stableKey: entity.stableKey,
  agentId: entity.agentId,
  agentName: entity.participant?.name,
  reportId: entity.reportId,
  reportContent: entity.isStreaming
    ? entity.streamingContent || entity.reportContent || ''
    : entity.reportContent || entity.content || '',
  reportVersion: entity.reportVersion,
  isLoading: entity.isStreaming,
  isMasterArtifact: entity.isMasterArtifact,
  reportFormat: entity.reportFormat,
})

const transformEntityToDelegation = (entity: DelegationEntity): ChatDelegation => ({
  id: entity.id,
  type: 'delegation',
  timestamp: resolveItemTimestamp(entity),
  stableKey: entity.stableKey,
  supervisorId: entity.supervisorId,
  targetList: entity.delegationTargetList,
  targetLabelList: entity.delegationTargetLabelList,
  isComplete: entity.isDelegationComplete,
  report: entity.delegationReport,
})

export const transformEntityToChatItem = (entity: ChatMessageEntity): ChatItem => {
  if (entity.type === 'message') return transformEntityToMessage(entity)
  if (entity.type === 'content-brief') return transformEntityToMessage(entity)
  if (entity.type === 'note') return transformEntityToNote(entity)
  if (entity.type === 'tool-call') return transformEntityToToolCall(entity)
  if (entity.type === 'agent-report') return transformEntityToAgentReport(entity)
  if (entity.type === 'delegation') return transformEntityToDelegation(entity)
  const exhaustive: never = entity
  return exhaustive
}
